import React, { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { ArrowLeft, Wallet, Upload, FileText, Download, Copy } from "lucide-react";
import { motion } from "framer-motion";

export default function SoftBradesco() {
  const [csvData, setCsvData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [saldoInvestFacil, setSaldoInvestFacil] = useState(0);
  const fileInputRef = useRef(null);
  
  // Estados para cálculo de médias
  const [finalCsvData, setFinalCsvData] = useState([]);
  const [averages, setAverages] = useState(null);
  const [isDragOverFinal, setIsDragOverFinal] = useState(false);
  const [isProcessingFinal, setIsProcessingFinal] = useState(false);
  const finalFileInputRef = useRef(null);

  // Função para remover acentos e converter para maiúsculo
  const removerAcentosEMaiusculo = (texto) => {
    const mapaAcentos = {
      'á': 'a', 'à': 'a', 'ã': 'a', 'â': 'a', 'ä': 'a',
      'é': 'e', 'è': 'e', 'ê': 'e', 'ë': 'e',
      'í': 'i', 'ì': 'i', 'î': 'i', 'ï': 'i',
      'ó': 'o', 'ò': 'o', 'õ': 'o', 'ô': 'o', 'ö': 'o',
      'ú': 'u', 'ù': 'u', 'û': 'u', 'ü': 'u',
      'ç': 'c', 'ñ': 'n',
      'Á': 'A', 'À': 'A', 'Ã': 'A', 'Â': 'A', 'Ä': 'A',
      'É': 'E', 'È': 'E', 'Ê': 'E', 'Ë': 'E',
      'Í': 'I', 'Ì': 'I', 'Î': 'I', 'Ï': 'I',
      'Ó': 'O', 'Ò': 'O', 'Õ': 'O', 'Ô': 'O', 'Ö': 'O',
      'Ú': 'U', 'Ù': 'U', 'Û': 'U', 'Ü': 'U',
      'Ç': 'C', 'Ñ': 'N'
    };
    
    let resultado = texto;
    Object.keys(mapaAcentos).forEach(char => {
      resultado = resultado.replace(new RegExp(char, 'g'), mapaAcentos[char]);
    });
    
    resultado = resultado
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\x20-\x7E]/g, '');
    
    return resultado.toUpperCase();
  };

  // Função para remover vírgulas da descrição
  const removerVirgulas = (texto) => {
    return texto.replace(/,/g, '');
  };

  // Função para processar valor monetário
  const parseValue = (value) => {
    if (!value || value === '---' || value === '') return 0;
    const cleanValue = value
      .replace(/R\$/g, '')
      .replace(/\s/g, '')
      .replace(/\./g, '')
      .replace(',', '.');
    return parseFloat(cleanValue) || 0;
  };

  // Função para formatar data no formato DDMMAAAA
  const formatarDataCSV = (dataOriginal) => {
    const match = dataOriginal.match(/(\d{2})\/(\d{2})\/(\d{4})/);
    if (match) {
      return `${match[1]}${match[2]}${match[3]}`;
    }
    return '01012025';
  };

  // Função para extrair competência do CSV (MMAAAA)
  const extrairCompetencia = () => {
    if (!csvData.length) {
      return '012025';
    }
    
    const primeiraData = csvData[0].data;
    const match = primeiraData.match(/\d{2}\/(\d{2})\/(\d{4})/);
    
    if (match) {
      const mes = match[1];
      const ano = match[2];
      return `${mes}${ano}`;
    }
    
    return '012025';
  };

  // Função para processar o CSV do Bradesco
  const processCSV = (csvText) => {
    const normalizedText = csvText
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n');

    const lines = normalizedText.split('\n').filter(line => line.trim());
    const data = [];
    
    let totalRecebimentos = 0;
    let valorTotalRecebimentos = 0;
    let totalPagamentos = 0;
    let valorTotalPagamentos = 0;
    let saldoInicial = null;
    let ultimoSaldoBanco = null;
    let ultimoSaldoInvestFacil = 0;
    let processandoExtrato = false;
    let processandoInvestFacil = false;

    console.log('📊 PROCESSANDO CSV DO BRADESCO');
    console.log('='.repeat(80));

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) {
        continue;
      }

      const normalizedLine = removerAcentosEMaiusculo(line);

      if (i < 20) {
        console.log(`🔍 [${i}] linha original:`, line);
        console.log(`🔍 [${i}] linha normalizada:`, normalizedLine);
      }

      if (normalizedLine.includes('ULTIMOS LANCAMENTOS')) {
        console.log('ℹ️ Sessão "Últimos Lançamentos" atingida. Encerrando leitura do extrato principal.');
        processandoExtrato = false;
        processandoInvestFacil = false;
        continue;
      }

      if (normalizedLine.includes('SALDOS INVEST FACIL')) {
        processandoExtrato = false;
        processandoInvestFacil = true;
        console.log('✅ Seção Invest Fácil detectada');
        continue;
      }

      if (normalizedLine.startsWith('DATA;HISTORICO;')) {
        processandoInvestFacil = true;
        continue;
      }

      if (normalizedLine.startsWith('DATA;') && normalizedLine.includes('SALDO (R$)')) {
        processandoExtrato = true;
        processandoInvestFacil = false;
        console.log('✅ Seção de extrato detectada');
        continue;
      }

      const values = line.split(';');
      const colunaData = values[0]?.trim() ?? '';
      const dataValida = /^\d{2}\/\d{2}\/\d{4}$/.test(colunaData);

      if (processandoInvestFacil && dataValida && values.length >= 3) {
        const valorInvest = parseValue(values[2]);
        ultimoSaldoInvestFacil = valorInvest;
        console.log(`💜 Invest Fácil detectado em ${colunaData}: ${valorInvest.toFixed(2)}`);
        continue;
      }

      if (!processandoInvestFacil && dataValida && values.length >= 6) {
        const lancamento = values[1] || '';
        const creditoBruto = parseValue(values[3]);
        const debitoBruto = parseValue(values[4]);
        const saldo = parseValue(values[5]);

        const credito = creditoBruto;
        const debito = debitoBruto;

        const lancamentoNormalizado = removerAcentosEMaiusculo(lancamento);

        if (lancamentoNormalizado.includes('SALDO ANTERIOR')) {
          if (saldoInicial === null) {
            saldoInicial = saldo;
            console.log(`💰 Saldo Inicial: ${saldoInicial.toFixed(2)}`);
          } else {
            console.log('ℹ️ Saldo anterior adicional ignorado para manter o primeiro valor identificado.');
          }
          continue;
        }

        if (lancamentoNormalizado.includes('SALDO INVEST FACIL')) {
          console.log('ℹ️ Linha de saldo Invest Fácil presente na sessão principal, ignorando.');
          continue;
        }

        const isEntrada = credito > 0;
        const isSaida = debito < 0;
        const valor = isEntrada ? credito : Math.abs(debito);

        if (valor > 0) {
          data.push({
            data: colunaData,
            descricao: lancamento,
            credito,
            debito,
            valor,
            saldo,
            isEntrada,
            isSaida,
            ehInvestFacilAplicacao: lancamentoNormalizado.includes('APLIC.INVEST FACIL'),
            ehInvestFacilResgate: lancamentoNormalizado.includes('RESGATE INVEST FACIL')
          });

          console.log(`✅ ${isEntrada ? 'Entrada' : 'Saída'} contabilizada: ${colunaData} | ${lancamento.substring(0, 40)} | Valor ${valor.toFixed(2)} | Saldo ${saldo.toFixed(2)}`);

          if (isEntrada) {
            totalRecebimentos++;
            valorTotalRecebimentos += valor;
          } else if (isSaida) {
            totalPagamentos++;
            valorTotalPagamentos += valor;
          }
          ultimoSaldoBanco = saldo;
        }
      }
    }

    const saldoInicialValor = saldoInicial !== null ? saldoInicial : 0;
    const saldoFinalCalculado = valorTotalRecebimentos - valorTotalPagamentos;
    const saldoBancoInformado = ultimoSaldoBanco !== null ? ultimoSaldoBanco : saldoFinalCalculado;

    console.log('='.repeat(80));
    console.log('📊 RESUMO DO PROCESSAMENTO');
    console.log('='.repeat(80));
    console.log('💰 Saldo Inicial:', saldoInicialValor.toFixed(2));
    console.log('📈 Total Recebimentos:', totalRecebimentos, '| Valor:', valorTotalRecebimentos.toFixed(2));
    console.log('📉 Total Pagamentos:', totalPagamentos, '| Valor:', valorTotalPagamentos.toFixed(2));
    console.log('💎 Saldo Invest Fácil:', ultimoSaldoInvestFacil.toFixed(2));
    console.log('✅ Saldo Final Calculado (Entradas - Saídas):', saldoFinalCalculado.toFixed(2));
    console.log('🏦 Saldo Final Informado (Banco):', saldoBancoInformado.toFixed(2));
    console.log('='.repeat(80));

    setSaldoInvestFacil(ultimoSaldoInvestFacil);

    return {
      data,
      summary: {
        totalTransacoes: data.length,
        totalRecebimentos,
        valorTotalRecebimentos,
        totalPagamentos,
        valorTotalPagamentos,
        saldoInicial: saldoInicialValor,
        saldoFinal: saldoFinalCalculado,
        saldoBancoInformado,
        saldoInvestFacil: ultimoSaldoInvestFacil
      }
    };
  };

  // Função para lidar com o upload do arquivo
  const handleFileUpload = (file) => {
    if (!file) return;
    
    setIsProcessing(true);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const csvText = e.target.result;
        const result = processCSV(csvText);
        setCsvData(result.data);
        setSummary(result.summary);
      } catch (error) {
        console.error('Erro ao processar CSV:', error);
        alert('Erro ao processar o arquivo CSV. Verifique o formato do arquivo.');
      } finally {
        setIsProcessing(false);
      }
    };
    
    reader.readAsText(file);
  };

  // Eventos de drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  // Função para formatar valores monetários
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Função para gerar CSV formatado para Questor
  const gerarCSVFormatado = () => {
    if (!csvData.length) {
      alert('Por favor, faça upload do arquivo CSV primeiro.');
      return;
    }

    const linhasCSV = [];
    
    console.log('📄 GERANDO CSV PARA QUESTOR - BRADESCO');
    console.log('='.repeat(80));

    // Processar todas as transações
    csvData.forEach((row, index) => {
      const dataFormatada = formatarDataCSV(row.data);

      const descricaoNormalizada = removerAcentosEMaiusculo(row.descricao || '');
      const ehSoftSolutions = descricaoNormalizada.includes('SOFT SOLUTIONS');

      let contaDebito = row.isEntrada ? 9 : 0;
      let contaCredito = row.isEntrada ? 2702 : (row.isSaida ? 9 : 0);

      if (descricaoNormalizada.includes('APLIC.INVEST FACIL')) {
        contaDebito = 58;
        contaCredito = 9;
      } else if (descricaoNormalizada.includes('RESGATE INVEST FACIL')) {
        contaDebito = 9;
        contaCredito = 58;
      } else if (row.isSaida && descricaoNormalizada.includes('JOAO PAULO FABRICIO')) {
        contaDebito = 4875;
        contaCredito = 9;
      }

      if (ehSoftSolutions) {
        if (row.isEntrada) {
          contaCredito = 4880;
        }

        if (row.isSaida) {
          contaDebito = 4880;
        }
      }

      if (row.isEntrada && descricaoNormalizada.includes('JOAO PAULO FABRICIO')) {
        contaCredito = 4875;
      }
      const valor = row.valor.toFixed(2);
      const zero = 0;
      
      // Processar descrição
      const descricaoOriginal = row.descricao.trim();
      const descricaoSemAcento = removerAcentosEMaiusculo(descricaoOriginal);
      const descricaoFinal = removerVirgulas(descricaoSemAcento);
      const descricao = `"${descricaoFinal}"`;

      // Formato: Data,ContaDebito,ContaCredito,Valor,Zero,Descrição
      const linha = `${dataFormatada},${contaDebito},${contaCredito},${valor},${zero},${descricao}`;
      linhasCSV.push(linha);
      
      if (index < 5) {
        console.log(`📄 Linha ${index + 1}: Débito=${contaDebito}, Crédito=${contaCredito}, Valor=${valor}, Tipo=${row.isEntrada ? 'ENTRADA' : 'SAÍDA'}`);
      }
    });

    console.log('📄 CSV gerado com', linhasCSV.length, 'linhas');
    console.log('='.repeat(80));

    // Criar e baixar o arquivo CSV
    const csvContent = linhasCSV.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `SOFT BRADESCO LANCAMENTOS CSV QUESTOR ${extrairCompetencia()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Função para baixar JSON dos dados
  const downloadJSON = () => {
    const dataToDownload = {
      summary,
      saldoInvestFacil,
      transactions: csvData,
      processedAt: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(dataToDownload, null, 2)], {
      type: 'application/json'
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `soft-bradesco-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Função para copiar JSON
  const copyJSON = () => {
    const dataToDownload = {
      summary,
      saldoInvestFacil,
      transactions: csvData,
      processedAt: new Date().toISOString()
    };
    
    navigator.clipboard.writeText(JSON.stringify(dataToDownload, null, 2))
      .then(() => alert('JSON copiado para a área de transferência!'))
      .catch(() => alert('Erro ao copiar JSON'));
  };

  // Função para processar CSV final e calcular médias
  const processFinalCSV = (csvText) => {
    const normalizedText = csvText
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n');

    const lines = normalizedText.split('\n').filter(line => line.trim());
    const data = [];
    
    let totalEntradas = 0;
    let totalFaturamento = 0;
    let primeiraData = null;
    let ultimaData = null;

    console.log('📊 PROCESSANDO CSV FINAL PARA CÁLCULO DE MÉDIAS');
    console.log('='.repeat(80));

    lines.forEach((line, index) => {
      const values = line.split(',');
      
      if (values.length >= 4) {
        const dataStr = values[0].trim();
        const contaDebito = parseInt(values[1].trim()) || 0;
        const contaCredito = parseInt(values[2].trim()) || 0;
        const valorStr = values[3].trim();
        const valor = parseFloat(valorStr) || 0;
        
        // Extrair data no formato DDMMAAAA
        if (dataStr.length === 8) {
          const dia = dataStr.substring(0, 2);
          const mes = dataStr.substring(2, 4);
          const ano = dataStr.substring(4, 8);
          const dataFormatada = `${dia}/${mes}/${ano}`;
          
          if (!primeiraData) {
            primeiraData = { dia, mes, ano, str: dataFormatada };
          }
          ultimaData = { dia, mes, ano, str: dataFormatada };
          
          // Média de Entrada: código 9 na coluna crédito (segunda coluna - índice 2)
          if (contaCredito === 9) {
            totalEntradas += valor;
            console.log(`✅ Entrada detectada: ${dataFormatada} | Crédito: ${contaCredito} | Valor: ${valor.toFixed(2)}`);
          }
          
          // Média de Faturamento: código 2702 na coluna crédito (segunda coluna - índice 2)
          if (contaCredito === 2702) {
            totalFaturamento += valor;
            console.log(`💰 Faturamento detectado: ${dataFormatada} | Crédito: ${contaCredito} | Valor: ${valor.toFixed(2)}`);
          }
          
          data.push({
            data: dataFormatada,
            contaDebito,
            contaCredito,
            valor,
            descricao: values[5] || ''
          });
        }
      }
    });

    // Calcular período de amostragem em meses
    let periodoMeses = 1;
    if (primeiraData && ultimaData) {
      const mesInicial = parseInt(primeiraData.mes);
      const anoInicial = parseInt(primeiraData.ano);
      const mesFinal = parseInt(ultimaData.mes);
      const anoFinal = parseInt(ultimaData.ano);
      
      periodoMeses = (anoFinal - anoInicial) * 12 + (mesFinal - mesInicial) + 1;
    }

    const mediaEntradas = totalEntradas / periodoMeses;
    const mediaFaturamento = totalFaturamento / periodoMeses;

    const mesesNomes = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 
                        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    
    const mesInicialNome = primeiraData ? mesesNomes[parseInt(primeiraData.mes) - 1] : '';
    const mesFinalNome = ultimaData ? mesesNomes[parseInt(ultimaData.mes) - 1] : '';

    console.log('='.repeat(80));
    console.log('📊 RESULTADO DO CÁLCULO DE MÉDIAS');
    console.log('='.repeat(80));
    console.log(`📅 Primeira Data: ${primeiraData?.str || 'N/A'}`);
    console.log(`📅 Última Data: ${ultimaData?.str || 'N/A'}`);
    console.log(`📆 Período de Amostragem: ${periodoMeses} meses`);
    console.log(`💵 Total de Entradas (Código 9): ${formatCurrency(totalEntradas)}`);
    console.log(`💰 Total de Faturamento (Código 2702): ${formatCurrency(totalFaturamento)}`);
    console.log(`📊 Média de Entradas: ${formatCurrency(mediaEntradas)}`);
    console.log(`📊 Média de Faturamento: ${formatCurrency(mediaFaturamento)}`);
    console.log('='.repeat(80));

    setFinalCsvData(data);
    setAverages({
      totalEntradas,
      totalFaturamento,
      mediaEntradas,
      mediaFaturamento,
      periodoMeses,
      primeiraData: primeiraData?.str || 'N/A',
      ultimaData: ultimaData?.str || 'N/A',
      mesInicialNome,
      mesFinalNome,
      anoInicial: primeiraData?.ano || '',
      anoFinal: ultimaData?.ano || ''
    });
  };

  // Função para lidar com upload do CSV final
  const handleFinalFileUpload = (file) => {
    if (!file) return;
    
    setIsProcessingFinal(true);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const csvText = e.target.result;
        processFinalCSV(csvText);
      } catch (error) {
        console.error('Erro ao processar CSV final:', error);
        alert('Erro ao processar o arquivo CSV final. Verifique o formato do arquivo.');
      } finally {
        setIsProcessingFinal(false);
      }
    };
    
    reader.readAsText(file);
  };

  // Eventos de drag and drop para CSV final
  const handleDragOverFinal = (e) => {
    e.preventDefault();
    setIsDragOverFinal(true);
  };

  const handleDragLeaveFinal = (e) => {
    e.preventDefault();
    setIsDragOverFinal(false);
  };

  const handleDropFinal = (e) => {
    e.preventDefault();
    setIsDragOverFinal(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFinalFileUpload(files[0]);
    }
  };

  const handleFinalFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFinalFileUpload(file);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Cabeçalho */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Link to={createPageUrl("Operations")}>
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Voltar para Operações
              </Button>
            </Link>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="p-3 bg-orange-100 rounded-xl">
              <Wallet className="w-8 h-8 text-orange-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">SOFT SOLUTIONS - BRADESCO</h1>
              <p className="text-gray-600 mt-1">Gerenciamento de operações contábeis Bradesco</p>
            </div>
          </div>
        </div>

        {/* Área de Upload */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload do Arquivo CSV
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                isDragOver 
                  ? 'border-orange-500 bg-orange-50' 
                  : 'border-gray-300 hover:border-orange-400'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="flex flex-col items-center gap-4">
                <div className="p-4 bg-orange-100 rounded-full">
                  <FileText className="w-8 h-8 text-orange-600" />
                </div>
                <div>
                  <p className="text-lg font-medium text-gray-900">
                    {isProcessing ? 'Processando arquivo...' : 'Arraste e solte o arquivo CSV aqui'}
                  </p>
                  <p className="text-gray-500 mt-1">
                    ou clique para selecionar o arquivo
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  className="flex items-center gap-2"
                  disabled={isProcessing}
                >
                  <Upload className="w-4 h-4" />
                  Selecionar Arquivo
                </Button>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
            />
          </CardContent>
        </Card>

        {/* Resumo Financeiro */}
        {summary && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
              <Card className="bg-indigo-50 border-indigo-200">
                <CardContent className="p-6">
                  <div>
                    <p className="text-indigo-600 text-sm font-medium">Saldo Inicial</p>
                    <p className="text-2xl font-bold text-indigo-700">
                      {formatCurrency(summary.saldoInicial)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-6">
                  <div>
                    <p className="text-blue-600 text-sm font-medium">Valor Recebimentos</p>
                    <p className="text-2xl font-bold text-blue-700">
                      {formatCurrency(summary.valorTotalRecebimentos)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-6">
                  <div>
                    <p className="text-red-600 text-sm font-medium">Valor Pagamentos</p>
                    <p className="text-2xl font-bold text-red-700">
                      {formatCurrency(summary.valorTotalPagamentos)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="p-6">
                  <div>
                    <p className="text-purple-600 text-sm font-medium">Saldo Invest Fácil</p>
                    <p className="text-2xl font-bold text-purple-700">
                      {formatCurrency(saldoInvestFacil)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-6">
                  <div>
                    <p className="text-green-600 text-sm font-medium">Saldo Final da Conta</p>
                    <p className="text-2xl font-bold text-green-700">
                      {formatCurrency(summary.saldoFinal)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Botões de Ação */}
            <div className="flex flex-wrap gap-4 mb-8">
              <Button onClick={downloadJSON} className="flex items-center gap-2">
                <Download className="w-4 h-4" />
                Baixar JSON
              </Button>
              <Button variant="outline" onClick={copyJSON} className="flex items-center gap-2">
                <Copy className="w-4 h-4" />
                Copiar JSON
              </Button>
              <Button 
                onClick={gerarCSVFormatado} 
                className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700"
              >
                <Download className="w-4 h-4" />
                Baixar CSV Formatado para Questor
              </Button>
            </div>

            {/* Tabela de Transações Processadas */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Transações Processadas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left p-3 font-medium text-gray-700">Tipo</th>
                        <th className="text-left p-3 font-medium text-gray-700">Data</th>
                        <th className="text-left p-3 font-medium text-gray-700">Descrição</th>
                        <th className="text-right p-3 font-medium text-gray-700">Valor</th>
                        <th className="text-right p-3 font-medium text-gray-700">Saldo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvData.map((row, index) => {
                        const descricaoLimpa = removerAcentosEMaiusculo(row.descricao.trim());
                        
                        return (
                          <tr key={index} className={`border-t ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                            <td className="p-3">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                row.isEntrada ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                              }`}>
                                {row.isEntrada ? 'Entrada' : 'Saída'}
                              </span>
                            </td>
                            <td className="p-3 text-gray-700">{row.data}</td>
                            <td className="p-3 text-gray-700 text-xs">{descricaoLimpa.substring(0, 60)}</td>
                            <td className={`p-3 text-right font-medium ${
                              row.isEntrada ? 'text-green-600' : 'text-orange-600'
                            }`}>
                              {formatCurrency(row.valor)}
                            </td>
                            <td className="p-3 text-right text-blue-600 font-medium">
                              {formatCurrency(row.saldo)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Seção de Cálculo de Médias */}
            <Card className="mt-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Cálculo de Médias - Upload do CSV Final
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                    isDragOverFinal 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-300 hover:border-blue-400'
                  }`}
                  onDragOver={handleDragOverFinal}
                  onDragLeave={handleDragLeaveFinal}
                  onDrop={handleDropFinal}
                  onClick={() => finalFileInputRef.current?.click()}
                >
                  <div className="flex flex-col items-center gap-4">
                    <div className="p-4 bg-blue-100 rounded-full">
                      <FileText className="w-8 h-8 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-lg font-medium text-gray-900">
                        {isProcessingFinal ? 'Processando CSV final...' : 'Arraste e solte o CSV final formatado aqui'}
                      </p>
                      <p className="text-gray-500 mt-1">
                        Para calcular médias de entrada e faturamento
                      </p>
                    </div>
                    <Button 
                      variant="outline" 
                      className="flex items-center gap-2"
                      disabled={isProcessingFinal}
                    >
                      <Upload className="w-4 h-4" />
                      Selecionar CSV Final
                    </Button>
                  </div>
                </div>
                <input
                  ref={finalFileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFinalFileSelect}
                  className="hidden"
                />
              </CardContent>
            </Card>

            {/* Resultados das Médias */}
            {averages && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mt-8"
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                    <CardContent className="p-6">
                      <div>
                        <p className="text-blue-600 text-sm font-medium mb-1">Período de Amostragem</p>
                        <p className="text-3xl font-bold text-blue-700 mb-2">
                          {averages.periodoMeses} meses
                        </p>
                        <p className="text-sm text-blue-600">
                          {averages.mesInicialNome}/{averages.anoInicial} a {averages.mesFinalNome}/{averages.anoFinal}
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                    <CardContent className="p-6">
                      <div>
                        <p className="text-green-600 text-sm font-medium mb-1">Média de Entrada</p>
                        <p className="text-3xl font-bold text-green-700">
                          {formatCurrency(averages.mediaEntradas)}
                        </p>
                        <p className="text-sm text-green-600">
                          Total: {formatCurrency(averages.totalEntradas)}
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                    <CardContent className="p-6">
                      <div>
                        <p className="text-purple-600 text-sm font-medium mb-1">Média de Faturamento</p>
                        <p className="text-3xl font-bold text-purple-700">
                          {formatCurrency(averages.mediaFaturamento)}
                        </p>
                        <p className="text-sm text-purple-600">
                          Total: {formatCurrency(averages.totalFaturamento)}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Memória de Cálculos */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Memória de Cálculos para Parecer Jurídico
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-gray-50 p-6 rounded-lg font-mono text-sm space-y-4">
                      <div>
                        <h3 className="font-bold text-lg mb-3 text-gray-900">CÁLCULO DE MÉDIAS - SOFT SOLUTIONS</h3>
                        <p className="text-gray-700 font-semibold">Banco Bradesco S/A AG: 00380 CC: 0041059-4</p>
                      </div>

                      <div className="border-t border-gray-300 pt-4">
                        <h4 className="font-bold text-gray-800 mb-2">1. PERÍODO DE AMOSTRAGEM</h4>
                        <p className="text-gray-700">Data Inicial: {averages.primeiraData}</p>
                        <p className="text-gray-700">Data Final: {averages.ultimaData}</p>
                        <p className="text-gray-700">Competência: {averages.mesInicialNome}/{averages.anoInicial} a {averages.mesFinalNome}/{averages.anoFinal}</p>
                        <p className="text-gray-700 font-bold">Total de Meses: {averages.periodoMeses} meses</p>
                      </div>

                      <div className="border-t border-gray-300 pt-4">
                        <h4 className="font-bold text-gray-800 mb-2">2. MÉDIA DE ENTRADA (RECEBIMENTOS)</h4>
                        <p className="text-gray-700">Total de Entradas no Período: {formatCurrency(averages.totalEntradas)}</p>
                        <p className="text-gray-700">Período de Amostragem: {averages.periodoMeses} meses</p>
                        <p className="text-gray-700">Cálculo: {formatCurrency(averages.totalEntradas)} ÷ {averages.periodoMeses}</p>
                        <p className="text-gray-700 font-bold text-lg mt-2">Média Mensal de Entrada: {formatCurrency(averages.mediaEntradas)}</p>
                      </div>

                      <div className="border-t border-gray-300 pt-4">
                        <h4 className="font-bold text-gray-800 mb-2">3. MÉDIA DE FATURAMENTO</h4>
                        <p className="text-gray-700">Total de Faturamento no Período: {formatCurrency(averages.totalFaturamento)}</p>
                        <p className="text-gray-700">Período de Amostragem: {averages.periodoMeses} meses</p>
                        <p className="text-gray-700">Cálculo: {formatCurrency(averages.totalFaturamento)} ÷ {averages.periodoMeses}</p>
                        <p className="text-gray-700 font-bold text-lg mt-2">Média Mensal de Faturamento: {formatCurrency(averages.mediaFaturamento)}</p>
                      </div>

                      <div className="border-t border-gray-300 pt-4">
                        <h4 className="font-bold text-gray-800 mb-2">4. RESUMO EXECUTIVO</h4>
                        <div className="bg-white p-4 rounded border border-gray-300">
                          <p className="text-gray-700 mb-2">
                            Com base na análise do extrato bancário da conta SOFT SOLUTIONS - BRADESCO, 
                            referente ao período de {averages.mesInicialNome}/{averages.anoInicial} a {averages.mesFinalNome}/{averages.anoFinal} 
                            ({averages.periodoMeses} meses), foram apurados os seguintes valores médios mensais:
                          </p>
                          <ul className="list-disc list-inside space-y-1 text-gray-700">
                            <li><strong>Média de Recebimentos:</strong> {formatCurrency(averages.mediaEntradas)}/mês</li>
                            <li><strong>Média de Faturamento:</strong> {formatCurrency(averages.mediaFaturamento)}/mês</li>
                          </ul>
                          <p className="text-gray-700 mt-3">
                            A memória de cálculo foi extraída integralmente dos lançamentos contábeis processados e está em conformidade com os registros.
                            As informações e demonstrativos podem ser integralmente conferidos e auditados nos relatórios contábeis.
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
